import type { ReactElement } from "react"
import { useContext } from "react"
import AlignContentOrg from "@onestore/hel/dist/components/organisms/AlignContentOrg"
import PushOrg from "@onestore/hel/dist/components/organisms/PushOrg"
import WidthOrg from "@onestore/hel/dist/components/organisms/WidthOrg"
import useBreakpoint from "@onestore/hel/dist/hooks/useBreakpoint"
import BaseContainer from "@gatsby-plugin-generic-page/components/BaseContainer"
import BaseSection from "@gatsby-plugin-generic-page/components/BaseSection"
import CtaElement from "@gatsby-plugin-generic-page/components/CtaElement"
import Header from "@gatsby-plugin-generic-page/components/Header"
import InfoBar from "@gatsby-plugin-generic-page/components/InfoBar"
import ProductBoxesGrid from "@gatsby-plugin-generic-page/components/ProductBoxesGrid"
import ProductBoxSchema from "@gatsby-plugin-generic-page/components/ProductBoxSchema"
import ProductBoxSlim from "@gatsby-plugin-generic-page/components/ProductBoxSlim"
import SwitchPeriodAndTogglePriceType from "@gatsby-plugin-generic-page/components/SwitchPeriodAndTogglePriceType"
import type { ProductBoxSectionFlatData } from "@gatsby-plugin-generic-page/fragments/productBoxSection"
import getPeriodsByPeriodVariants from "@gatsby-plugin-generic-page/helpers/getPeriodsByPeriodVariants"
import { isProductBoxVisibleForPeriod } from "@gatsby-plugin-generic-page/helpers/isProductBoxVisibleForPeriod"
import { useIsomorphicLayoutEffect } from "@gatsby-plugin-generic-page/hooks/useIsomorphicLayoutEffect"
import ContentSwitcher from "~/components/ContentSwitcher"
import SwitchContentContext from "~/context/SwitchContentContext"
import isEmpty from "~/lib/isEmpty"

export type ProductBoxPeriodsSwitcherSectionProps =
  ProductBoxSectionFlatData & {
    hasAnyOuterFooter: boolean
    // See ProductBoxSection's containerClassName for the rationale.
    containerClassName?: string
  }

export default function ProductBoxPeriodsSwitcherSection({
  header,
  boxes,
  sectionSettings,
  ctaElement,
  hasTogglePriceType,
  periodsSwitcher,
  hasUpsellDrawer,
  infoBar,
  hasAnyOuterFooter,
  containerClassName,
}: ProductBoxPeriodsSwitcherSectionProps): ReactElement<ProductBoxPeriodsSwitcherSectionProps> | null {
  const productBoxesNumber = boxes.length
  const isSmallAndMediumScreen = useBreakpoint("small", "large")

  const context = useContext(SwitchContentContext)
  const periods = getPeriodsByPeriodVariants(periodsSwitcher?.periods)

  useIsomorphicLayoutEffect()(() => {
    if (!isEmpty(periodsSwitcher)) {
      context?.setPeriodVariants(periodsSwitcher.periods)
    }

    if (periodsSwitcher?.defaultPeriodValue) {
      context?.setCurrentKey(periodsSwitcher.defaultPeriodValue)
    }
  }, [])

  if (!periodsSwitcher || isEmpty(periods)) {
    return null
  }

  return (
    <>
      <ProductBoxSchema boxes={boxes} />

      <BaseSection
        {...sectionSettings}
        hasOverflowHidden={isSmallAndMediumScreen}
        role="region"
        aria-label="Selling Section"
      >
        <BaseContainer
          {...sectionSettings?.containerSettings}
          className={containerClassName}
        >
          {header ? (
            <PushOrg bottomSpace={hasTogglePriceType ? 4 : 8}>
              <Header {...header} />
            </PushOrg>
          ) : null}

          {hasTogglePriceType || !isEmpty(periods) ? (
            <SwitchPeriodAndTogglePriceType
              hasTogglePriceType={hasTogglePriceType}
              text={periodsSwitcher.text}
              bottomText={periodsSwitcher.bottomText}
              defaultPeriod={periodsSwitcher.defaultPeriodValue}
              initPeriods={periods}
              showAdditionalButtonDescription
            />
          ) : null}

          <ContentSwitcher
            items={periods}
            selected={context?.currentKey || periodsSwitcher.defaultPeriodValue}
          >
            {(selected) => {
              // A box with an AX Configuration hides itself when none of its
              // plans offer the selected period — size the grid to what will
              // actually render for this period, not the section's full box
              // count, or hidden boxes leave a gap. Falls back to the full
              // count in the (unreachable in practice) case every box hides.
              const visibleBoxesNumber =
                boxes.filter((box) =>
                  isProductBoxVisibleForPeriod(box, selected.period)
                ).length || productBoxesNumber

              return (
                <ProductBoxesGrid numberOfColumns={visibleBoxesNumber}>
                  {boxes.map((box, index) => (
                    <ProductBoxSlim
                      key={box.id}
                      productBox={box}
                      period={selected.period}
                      index={index}
                      hasAnyOuterFooter={hasAnyOuterFooter}
                      hasUpsellDrawer={hasUpsellDrawer}
                    />
                  ))}
                </ProductBoxesGrid>
              )
            }}
          </ContentSwitcher>

          {!isEmpty(ctaElement) ? (
            <PushOrg topSpace={6}>
              <AlignContentOrg horizontalAlign="center">
                <CtaElement ctaElement={ctaElement} />
              </AlignContentOrg>
            </PushOrg>
          ) : null}

          {!isEmpty(infoBar) ? (
            <PushOrg topSpace={5}>
              <AlignContentOrg horizontalAlign="center">
                <WidthOrg widthSpace={120}>
                  <InfoBar {...infoBar[0]} />
                </WidthOrg>
              </AlignContentOrg>
            </PushOrg>
          ) : null}
        </BaseContainer>
      </BaseSection>
    </>
  )
}
